import { getRoutesCombinedFilter, getHandledByFilter } from 'utils/filters';
import { finishedStatuses } from 'utils/journey';
import moment from 'moment';

import { getBusinessFilter } from 'utils/filters';

/**
 * @param filters {Object}
 */
export function adaptFiltersForBackend(filters) {
  const { tasks_handled_by, routes_combined, shipper, prio, transitStatus, business, ftb } =
    filters;

  const shipperFilter = shipper ? shipper.id : undefined;
  const prioFilter = prio ? String(prio) : undefined;
  const routesCombinedFilter = routes_combined
    ? JSON.stringify(getRoutesCombinedFilter(routes_combined))
    : undefined;
  const handledByFilter = getHandledByFilter(tasks_handled_by);
  const businessFilter = getBusinessFilter(business);

  return {
    routes: routesCombinedFilter,
    shipper: shipperFilter,
    shipper_is_prio: prioFilter,
    handled_by: handledByFilter,
    shipper_is_ftb: ftb,
    transit_status: transitStatus,
    ...businessFilter,
  };
}

/**
 * @param task {Object}
 * @param filters {Object}
 */
export function checkFiltersOnGroupOfTasks({ groupOfTasks, filters }) {
  const {
    shipper_is_prio,
    business_type: businessType,
    handled_by,
    route,
    shipper_orders,
    status,
    pickup_time_from,
    has_ftl_shipper_order,
  } = groupOfTasks || {};

  return passesFilterConditions([
    {
      filter: filters.shipper,
      value: shipper_orders,
      conditions: conditions.shipper,
    },
    {
      filter: filters.prio,
      value: shipper_is_prio,
      conditions: conditions.prio,
    },
    {
      filter: filters.routes_combined,
      value: { ...route, businessType },
      conditions: conditions.route,
    },
    {
      filter: filters.tasks_handled_by,
      value: handled_by,
      conditions: conditions.owner,
    },
    {
      filter: filters.transitStatus,
      value: { status, pickup_time_from },
      conditions: conditions.transit_status,
    },
    {
      filter: filters.business,
      value: has_ftl_shipper_order,
      conditions: conditions.business,
    },
  ]);
}

const conditions = {
  shipper: (shipperFilter, shipperOrders) => {
    const shipperIds = shipperOrders?.map((so) => so.shipper_id);

    return shipperIds?.includes(shipperFilter);
  },
  prio: (_, shipperIsPrio) => {
    return shipperIsPrio;
  },
  /**
   * Multiple filters can be applied within the filter, checks all the applied ones and returns
   * true if ANY of them is true, allows to combine journeys with different routes.
   * @param routesFilter {Array} [{value: 'BCN'}, {value: 'long_haul'}...]
   * @param country {String} journey.route.country
   * @param origin {String} journey.route.origin
   * @param businessType {String} journey.business_type
   * @returns {boolean|*}
   */
  route: (routesFilter, { country, origin, businessType }) => {
    const journeyPayload = { country, origin, businessType };
    const filterValues = routesFilter.map((route) => route.value);

    const allRouteFilters = {
      LH_ES: filterValues.includes('long_haul'),
      LH_FR: filterValues.includes('long_haul_fr'),
      INT: filterValues.includes('international'),
      SH: filterValues.some(
        (value) => !['long_haul', 'long_haul_uk', 'long_haul_fr', 'international'].includes(value)
      ),
    };

    const evaluations = {
      INT: ({ businessType }) => businessType === 'INT',
      LH_ES: ({ country, businessType }) => country === 'ES' && businessType === 'LH',
      LH_FR: ({ country, businessType }) => country === 'FR' && businessType === 'LH',
      SH: ({ origin, businessType }) =>
        filterValues.some((route) => route === origin) && businessType === 'SH',
    };

    const activeFilters = Object.entries(allRouteFilters).filter(([filter, active]) => active);
    const evaluationsResult = activeFilters.map(([filter]) => evaluations[filter](journeyPayload));

    return evaluationsResult.some((result) => !!result);
  },
  owner: (handledByFilter, journeyOwner) => {
    if (handledByFilter === 'all') return true;
    if (handledByFilter === 'no_owner') return !journeyOwner || journeyOwner?.is_bot;
    return handledByFilter?.id === journeyOwner?.id;
  },
  transit_status: (statusFilter, { status, pickup_time_from }) => {
    if (!statusFilter) return true;

    const evaluations = {
      pre_transit: () => moment(pickup_time_from).isAfter(moment(), 'day'),
      post_transit: () => finishedStatuses.includes(status),
      in_transit: function () {
        return !this.pre_transit() && !this.post_transit();
      },
    };

    return evaluations[statusFilter]();
  },
  business: (businessFilter, hasFtlShipperOrder) => {
    return businessFilter === 'FTL' ? hasFtlShipperOrder : !hasFtlShipperOrder;
  },
};

/**
 * @param filterChecks {Array}
 */
function passesFilterConditions(filterChecks) {
  const filterResults = filterChecks.map(({ filter, value, conditions }) => {
    if (!filter || (Array.isArray(filter) && !filter.length)) return true;

    return conditions(filter, value);
  });

  return filterResults.every((value) => value === true);
}
